
import React, { useState, useCallback, useEffect } from 'react'
import { notifyError } from '../../utils/notifyError'
import CustomHeader from '../../components/CustomHeader'
import PAGE_TITLES from '../../consts/titles'
import { Box, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material'
import PageHeader from '../../components/PageHeader'
import GenericButton from '../../components/GenericButton'
import { ButtonType } from '../../components/GenericButton/styles'
import GenericModal from '../../components/GenericModal'
import CreateAndEditProject from '../../features/DistributionSeriesAndPlan/Projects/CreateAndEditProject'
import { useQuery } from '@tanstack/react-query'
import { type GetAllDistributionSeriesResult } from '../../APIManager/DistributionManager'
import { PermissionEnum } from '../../models/DataObject'
import { DistributionManager } from '../../APIManager'
import { SandboxSeriesFilter } from '../../APIManager/DistributionManager'
import LoadingPageV2 from '../LoadingPageV2'
import { ProjectsSidebar } from '../../features/DistributionSeriesAndPlan/Projects/ProjectsSidebar'
import { DistributionPlanTable } from '../../features/DistributionSeriesAndPlan/DistributionPlanTable'
import { useAppSearchParams } from '../../hooks/useAppSearchParams'
import CustomTooltip from '../../components/CustomTooltip'
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined'

export enum ProjectScopeEnum {
  MY_SURVEY_PROJECTS = 'My Survey Projects',
  ALL_SURVEY_PROJECTS = 'All Survey Projects'
}

export const ProjectPage: React.FC = () => {
  const { searchParams, setUrlParams } = useAppSearchParams()
  const selectedScope = searchParams.get('scope') as ProjectScopeEnum || ProjectScopeEnum.MY_SURVEY_PROJECTS
  const [showProjectSidebar, setShowProjectSidebar] = useState(true)
  const [showCreateSurveyProjectModal, setShowCreateSurveyProjectModal] = useState<boolean>(false)
  const [scope, setScope] = useState<ProjectScopeEnum>(selectedScope)

  const theme = useTheme()
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isDesktopSidebar = !isMediumScreen && showProjectSidebar

  // • Runs when `isMediumScreen` changes
  // • If the viewport is medium, flips (toggles) the sidebar open/closed state
  // • Note: this toggles, it doesn’t always “collapse” — can cause unexpected behavior
  useEffect(() => {
    if (isMediumScreen) {
      setShowProjectSidebar(!showProjectSidebar)
    }
  }, [isMediumScreen])

  const {
    data: projectsResult,
    isLoading: isProjectsLoading,
    isPending: isProjectsFetching,
    isError: isProjectsError,
    error: projectsError
  } = useQuery<GetAllDistributionSeriesResult>({
    queryKey: ['projects', scope],
    // CR-341: `GET /distribution_series/` defaults to `sandbox=exclude` — real projects only.
    // That is the right default everywhere else, but it drops a project whose every term is a
    // rehearsal out of the project lists, and these lists are the only route to the page that
    // switches sandbox back off. Both scopes ask, because `/public/` carries the same default and
    // honours the same three values. `include` is not a permission: the backend's visibility
    // filter runs ahead of it, so a caller without a Global-level role still sees no rehearsals.
    queryFn: async () => await DistributionManager.getAllDistributionSeries(
      { sandbox: SandboxSeriesFilter.Include },
      { isPublic: scope === ProjectScopeEnum.ALL_SURVEY_PROJECTS }
    ),
    retry: false,
    enabled: true
  })

  useEffect(() => {
    if (isProjectsError) notifyError(projectsError, { fallbackMessage: 'Failed to load survey projects. Please try again.' })
  }, [isProjectsError])
  const projects = projectsResult?.data ?? []
  const permissions = projectsResult?.permissions
  const projectData = projects?.map(item => item?.data)
  const selectedProjectId = searchParams.get('projectId')
  const selectedProject = projectData?.find(project => project.distributionSeriesId === selectedProjectId)

  const handleShowCreateSurveyProjectModal = useCallback(() => { setShowCreateSurveyProjectModal(true) }, [])
  const handleCloseModal = useCallback(() => { setShowCreateSurveyProjectModal(false) }, [])
  const disableCreateProject = permissions?.includes(PermissionEnum.CREATE) || permissions?.includes(PermissionEnum.EDIT)
  const isLoading = isProjectsLoading || isProjectsFetching

  const handleScopeChange = (newScope: ProjectScopeEnum): void => {
    setScope(newScope)
    setUrlParams({ scope: newScope })
  }

  const createProjectTooltip = !disableCreateProject ? 'You do not have permission to create a project' : ''

  if (isLoading) {
    return <LoadingPageV2 title={PAGE_TITLES.DISTRIBUTION_SERIES} />
  }

  return (
    <Box width="100%" display='flex' flexDirection='column' >
      <CustomHeader title={PAGE_TITLES.DISTRIBUTION_SERIES} />
      <PageHeader
        title={PAGE_TITLES.DISTRIBUTION_SERIES}
        isCollapsible={false}
        controls={
          <CustomTooltip title={createProjectTooltip} placement="top">
            <GenericButton
              buttonType={ButtonType.Primary}
              label="Create Project"
              sx={{ whiteSpace: 'nowrap' }}
              onClick={handleShowCreateSurveyProjectModal}
              disabled={!disableCreateProject || isLoading}
            />
          </CustomTooltip>
        }
      />
      <Box display={'flex'} alignItems={'center'}>
        <CustomTooltip title={showProjectSidebar ? 'Collapse project sidebar' : 'Expand project sidebar'} placement="top">
          <GenericButton
            icon={<ViewSidebarOutlinedIcon />}
            onClick={() => { setShowProjectSidebar(!showProjectSidebar) }}
          />
        </CustomTooltip>

        <Tabs
          value={scope}
          onChange={(_e, newValue) => { handleScopeChange(newValue) }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {Object.values(ProjectScopeEnum).map((view) => (
            <Tab
              key={view}
              label={view}
              value={view}
              sx={{
                borderRadius: 24,
                fontWeight: 600,
                textTransform: 'none'
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 3,
          width: '100%',
          transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <Box width={isDesktopSidebar ? '20%' : 0} sx={{ transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <ProjectsSidebar
            projects={projectData}
            permissions={permissions}
            scope={scope}
            onScopeChange={handleScopeChange}
            showProjectSidebar={showProjectSidebar}
            setShowProjectSidebar={setShowProjectSidebar }
          />
        </Box>
        <Box width={!isDesktopSidebar ? '100%' : '80%'} sx={{ transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <DistributionPlanTable
            scope={scope}
            canEditReportPermissions={Boolean(permissions?.includes(PermissionEnum.EDIT))}
            seriesTitle={selectedProject?.title}
            isInstructorSurveyDateChangeEnabled={Boolean(selectedProject?.isInstructorSurveyDateChangeEnabled)}
          />
        </Box>
      </Box>
      <GenericModal
        isOpen={showCreateSurveyProjectModal}
        maxWidth="sm"
        title="Create Survey Project"
        onClose={handleCloseModal}
        action={false}
      >
        <CreateAndEditProject handleCloseModal={handleCloseModal} />
      </GenericModal>
    </Box>
  )
}
